// Components
import Button from '../Button/Button';
import Picto from '../Picto/Picto';

// Utils
import cn from '../../../utils/cn';

// Styles
import cornerStyles from '../../../styles/tableCardCorners.module.css';
import styles from './TableGroup.module.css';

/**
 * TableGroup component – wrapper for a table with header & body
 * @param {object} props
 * @param {number|string} [props.width='100%'] - CSS width (number => px)
 * @param {React.ReactNode} props.children - Table content (thead/tbody)
 * @param {boolean} [props.childrenIsVisible=true] - Whether to display the children (fallback if no accordion state)
 * @param {string} props.title - Group title
 * @param {React.ReactNode | null} [props.titleAction=null] - Action title
 * @param {string} [props.className] - Additional classes
 * @param {boolean} [props.isOpen] - Whether the group is open (controlled by parent)
 * @param {() => void} [props.onToggle] - Callback when toggle button is clicked
 * @param {React.ReactNode} [props.headerStats=null] - Group-level stat row (under title, above table)
 * @param {React.ReactNode} [props.childrenFooter=null] - Per-group stats footer; stays visible when the group is collapsed
 * @param {React.Ref<HTMLDivElement>} [props.headerRef] - Ref on the group header (row virtualizer scrollMargin)
 * @returns {JSX.Element}
 */
function TableGroup({
  width = '100%',
  children,
  childrenIsVisible = true,
  title,
  titleAction = null,
  className = '',
  isOpen,
  onToggle,
  headerStats = null,
  childrenFooter = null,
  headerRef,
}) {
  const computedWidth = typeof width === 'number' ? `${width}px` : width;
  const isChildrenVisible = isOpen !== undefined ? isOpen : childrenIsVisible;

  return (
    <div className={`${styles['table-group-wrapper']} ${className}`} style={{ width: computedWidth }}>
      <div className={styles['table-group-header']} ref={headerRef}>
        <div className={cn([styles['table-group-header-title'], cornerStyles['corner-tl']])}>
          <h3 className={styles['table-group-header-title-checkbox']}>
            {titleAction}
            {title}
          </h3>
          <div className={styles['table-group-header-actions']}>
            <Button
              color="neutral00"
              size="small"
              onClick={onToggle || (() => {})}
              className={styles['table-group-header-actions-button']}
            >
              <Picto icon={isChildrenVisible ? 'unfold-less' : 'unfold-more'} />
            </Button>
          </div>
        </div>
        {headerStats}
      </div>
      {isChildrenVisible && (
        <div
          className={cn([
            styles['table-group-body'],
            !childrenFooter && styles['table-group-body-rounded-bottom'],
          ])}
        >
          <table className={styles['table-group-body-table']}>
            {children}
          </table>
        </div>
      )}
      {childrenFooter ? (
        <div className={styles['table-group-footer']}>
          <table className={styles['table-group-footer-table']}>
            {childrenFooter}
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default TableGroup;
